import { css } from "@emotion/react";
import { Fragment, Suspense, useCallback, useEffect, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import type { BlockerFunction } from "react-router";
import { useBlocker, useSearchParams } from "react-router";

import {
  Button,
  Flex,
  Icon,
  Icons,
  Keyboard,
  Loading,
  PageHeader,
  Text,
  View,
  VisuallyHidden,
} from "@phoenix/components";
import { ConfirmNavigationDialog } from "@phoenix/components/ConfirmNavigation";
import { TemplateFormats } from "@phoenix/components/templateEditor/constants";
import {
  PlaygroundProvider,
  usePlaygroundContext,
} from "@phoenix/contexts/PlaygroundContext";
import { usePreferencesContext } from "@phoenix/contexts/PreferencesContext";
import { useModifierKey } from "@phoenix/hooks/useModifierKey";
import { ConfirmExperimentNavigationDialog } from "@phoenix/pages/playground/ConfirmExperimentNavigationDialog";
import { PlaygroundExamplePage } from "@phoenix/pages/playground/PlaygroundExamplePage";
import type { PromptParam } from "@phoenix/pages/playground/playgroundURLSearchParamsUtils";
import { setPromptParams } from "@phoenix/pages/playground/playgroundURLSearchParamsUtils";
import type { PlaygroundProps } from "@phoenix/store";

import type { PlaygroundQuery } from "./__generated__/PlaygroundQuery.graphql";
import { PlaygroundGeneralEvaluatorSelect } from "./PlaygroundGeneralEvaluatorSelect";
import { NUM_MAX_PLAYGROUND_INSTANCES } from "./constants";
import { NoInstalledProvider } from "./NoInstalledProvider";
import { PlaygroundComparisonSummary } from "./PlaygroundComparisonSummary";
import { PlaygroundConfigButton } from "./PlaygroundConfigButton";
import { PlaygroundCredentialsDropdown } from "./PlaygroundCredentialsDropdown";
import { PlaygroundDatasetSection } from "./PlaygroundDatasetSection";
import { PlaygroundDatasetSelect } from "./PlaygroundDatasetSelect";
import { PlaygroundInput } from "./PlaygroundInput";
import { PlaygroundOutput } from "./PlaygroundOutput";
import { PlaygroundRunButton } from "./PlaygroundRunButton";
import { PlaygroundTemplate } from "./PlaygroundTemplate";
import { TemplateFormatRadioGroup } from "./TemplateFormatRadioGroup";

// ─── Minimum prompt panel width ───
const PLAYGROUND_PROMPT_PANEL_MIN_WIDTH = 480;

// ─── Layout & Styling ─────────────────────────────────────────────────────────

const playgroundWrapCSS = css`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  height: 100%;
  background-color: var(--global-background-color-default, #f8f9fa);
`;

const playgroundBodyCSS = css`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 16px 20px 20px 20px;
  gap: 16px;
  overflow: hidden;
`;

const playgroundColumnsCSS = css`
  display: flex;
  flex-direction: row;
  flex: 1 1 auto;
  min-height: 0;
  gap: 20px;
  overflow: hidden;
`;

// ── Left Column (Prompt Editors Card) ─────────────────────────────────────────
const leftColumnCSS = css`
  flex: 1 1 55%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--global-color-surface-lowest, #ffffff);
  border: 1px solid var(--global-border-color-default, #e1e3e4);
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

  .theme--dark & {
    background-color: #1e293b !important;
    border-color: #334155 !important;
  }
`;

const leftColumnHeaderCSS = css`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid var(--global-border-color-default, #e1e3e4);
  background-color: var(--global-color-surface-container-low, #f3f4f5);

  .theme--dark & {
    background-color: #0f172a !important;
    border-color: #334155 !important;
  }
`;

const sectionLabelCSS = css`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--global-text-color-700, #575e70);
  white-space: nowrap;

  .theme--dark & {
    color: #cbd5e1 !important;
  }
`;

const leftColumnScrollCSS = css`
  flex: 1 1 auto;
  overflow: auto;
  padding: 16px;
  scrollbar-gutter: stable;
  box-sizing: border-box;
`;

// ── Right Column (Inputs & Output Card) ──────────────────────────────────────
const rightColumnCSS = css`
  flex: 0 0 45%;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--global-color-surface-lowest, #ffffff);
  border: 1px solid var(--global-border-color-default, #e1e3e4);
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

  .theme--dark & {
    background-color: #1e293b !important;
    border-color: #334155 !important;
  }
`;

const rightColumnHeaderCSS = css`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid var(--global-border-color-default, #e1e3e4);
  background-color: var(--global-color-surface-container-low, #f3f4f5);
  font-size: 14px;
  font-weight: 600;
  color: var(--global-text-color-900, #191c1d);

  .theme--dark & {
    background-color: #0f172a !important;
    border-color: #334155 !important;
    color: #ffffff !important;
  }
`;

const rightColumnScrollCSS = css`
  flex: 1 1 auto;
  overflow-y: auto;
`;

// ── Horizontal Layout for Outputs ─────────────────────────────────────────────
const outputsRowScrollCSS = css`
  display: flex;
  flex-direction: row;
  gap: 12px;
  overflow-x: auto;
  width: 100%;
  padding-bottom: 4px;
`;

const outputPanelCSS = css`
  flex: 1 1 0px;
  min-width: 260px;
  box-sizing: border-box;
`;

// ── Bottom Toolbar ───────────────────────────────────────────────────────────
const bottomToolbarCSS = css`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background-color: #ffffff;
  border: 1px solid var(--ac-global-border-color-default, #e1e3e4);
  border-radius: 16px;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
`;

const toolbarDividerCSS = css`
  width: 1px;
  height: 20px;
  background: var(--ac-global-border-color-default, #e1e3e4);
  flex: 0 0 auto;
`;

const readyDotCSS = css`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--ac-global-color-primary, #00685f);
  flex-shrink: 0;
`;

// ─── Root Component ───────────────────────────────────────────────────────────

export function Playground(
  props: Partial<PlaygroundProps> & {
    datasetId?: string | null;
    selectedDatasetEvaluatorIds?: string[];
  }
) {
  const [searchParams] = useSearchParams();
  const experimentId = searchParams.get("experimentId");
  const datasetId = experimentId
    ? (props.datasetId ?? null)
    : searchParams.get("datasetId");

  const { modelProviders } = useLazyLoadQuery<PlaygroundQuery>(
    graphql`
      query PlaygroundQuery {
        modelProviders {
          name
          dependenciesInstalled
          dependencies
        }
      }
    `,
    {}
  );

  const modelConfigByProvider = usePreferencesContext(
    (state) => state.modelConfigByProvider
  );
  const playgroundStreamingEnabled = usePreferencesContext(
    (state) => state.playgroundStreamingEnabled
  );
  const hasInstalledProvider = modelProviders.some(
    (provider) => provider.dependenciesInstalled
  );

  if (!hasInstalledProvider) {
    return <NoInstalledProvider availableProviders={modelProviders} />;
  }

  return (
    <PlaygroundProvider
      {...props}
      datasetId={datasetId}
      streaming={playgroundStreamingEnabled}
      modelConfigByProvider={modelConfigByProvider}
    >
      <div css={playgroundWrapCSS}>
        <View borderBottomColor="default" borderBottomWidth="thin">
          <PageHeader
            title="Playground"
            subTitle="Test and iterate on your prompts in real-time"
            extra={<PlaygroundHeaderExtra />}
          />
        </View>
        <PlaygroundContent />
      </div>
      <Suspense>
        <PlaygroundExamplePage />
      </Suspense>
    </PlaygroundProvider>
  );
}

// ─── Header Right Controls ───────────────────────────────────────────────────

function PlaygroundHeaderExtra() {
  const [searchParams] = useSearchParams();
  const storeDatasetId = usePlaygroundContext((state) => state.datasetId);
  const experimentId = searchParams.get("experimentId");
  const datasetId = experimentId
    ? storeDatasetId
    : searchParams.get("datasetId");
  const isDatasetMode = datasetId != null;

  return (
    <Flex direction="row" gap="size-100" alignItems="center">
      <PlaygroundDatasetSelect />
      <AddPromptButton />
      {!isDatasetMode && <PlaygroundGeneralEvaluatorSelect />}
      <PlaygroundConfigButton />
    </Flex>
  );
}

// ─── Compare Button ───────────────────────────────────────────────────────────

function AddPromptButton() {
  const addInstance = usePlaygroundContext((state) => state.addInstance);
  const instances = usePlaygroundContext((state) => state.instances);
  const numInstances = instances.length;
  const isRunning = instances.some((instance) => instance.activeRunId != null);
  return (
    <Button
      size="S"
      aria-label="add prompt"
      leadingVisual={<Icon svg={<Icons.PlusCircleOutline />} />}
      isDisabled={numInstances >= NUM_MAX_PLAYGROUND_INSTANCES || isRunning}
      onPress={() => {
        addInstance();
      }}
    >
      Compare
    </Button>
  );
}

// ─── Main Content (Two-Column Layout) ────────────────────────────────────────

function PlaygroundContent() {
  const templateFormat = usePlaygroundContext((state) => state.templateFormat);
  const [searchParams, setSearchParams] = useSearchParams();
  const storeDatasetId = usePlaygroundContext((state) => state.datasetId);
  const experimentId = searchParams.get("experimentId");
  const datasetId = experimentId
    ? storeDatasetId
    : searchParams.get("datasetId");

  const serializedSplitIds = searchParams.getAll("splitId").join("\0");
  const splitIds = useMemo(() => {
    if (serializedSplitIds.length === 0) return undefined;
    return serializedSplitIds.split("\0");
  }, [serializedSplitIds]);

  const isDatasetMode = datasetId != null;

  const isRunning = usePlaygroundContext((state) =>
    state.instances.some((instance) => instance.activeRunId != null)
  );
  const runningExperiment = usePlaygroundContext((state) => {
    const instance = state.instances.find(
      (inst) => inst.activeRunId != null && inst.experiment != null
    );
    return instance?.experiment ?? null;
  });
  const anyDirtyPromptInstances = usePlaygroundContext((state) =>
    Object.values(state.dirtyInstances).some((dirty) => dirty)
  );

  const instanceIds = usePlaygroundContext(
    (state) => state.instances.map((instance) => instance.id),
    (left, right) =>
      left.length === right.length &&
      left.every((id, index) => id === right[index])
  );

  const playgroundDatasetStateByDatasetId = usePlaygroundContext(
    (state) => state.stateByDatasetId
  );
  const playgroundDatasetState = datasetId
    ? playgroundDatasetStateByDatasetId[datasetId]
    : null;
  const { appendedMessagesPath, availablePaths } = playgroundDatasetState ?? {};

  const instancePromptParams = usePlaygroundContext(
    (state) =>
      state.instances
        .map((instance): PromptParam | null =>
          instance.prompt
            ? {
                promptId: instance.prompt.id,
                promptVersionId: instance.prompt.version,
                tagName: instance.prompt.tag,
              }
            : null
        )
        .filter((param): param is PromptParam => param != null),
    (left, right) =>
      left.length === right.length &&
      left.every(
        (param, index) =>
          param.promptId === right[index].promptId &&
          param.promptVersionId === right[index].promptVersionId &&
          param.tagName === right[index].tagName
      )
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        setPromptParams({ searchParams: next, prompts: instancePromptParams });
        return next;
      },
      { replace: true }
    );
  }, [instancePromptParams, setSearchParams]);

  const shouldBlockUnload = useCallback(
    ({ currentLocation, nextLocation }: Parameters<BlockerFunction>[0]) => {
      const goingToNewPage = currentLocation.pathname !== nextLocation.pathname;
      return (isRunning || anyDirtyPromptInstances) && goingToNewPage;
    },
    [isRunning, anyDirtyPromptInstances]
  );
  const blocker = useBlocker(shouldBlockUnload);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = true;
    };
    if (isRunning) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [isRunning]);

  const numInstances = instanceIds.length;
  const outputIs2x2Grid = numInstances === 4;

  return (
    <Fragment key="playground-content">
      <div css={playgroundBodyCSS}>
        {/* ══════════════════════════════════════════════════════════
            TWO-COLUMN MAIN AREA
          ══════════════════════════════════════════════════════════ */}
        <div css={playgroundColumnsCSS}>
          {/* ── LEFT COLUMN: Prompt Card ────────────────────────── */}
          <div css={leftColumnCSS}>
            <div css={leftColumnScrollCSS}>
              <Flex direction="row" gap="size-200" width="100%">
                {instanceIds.map((instanceId) => (
                  <View
                    flex="1 1 0px"
                    key={`${instanceId}-prompt`}
                    minWidth={
                      numInstances === 1
                        ? "100%"
                        : numInstances === 2
                        ? "calc(50% - 8px)"
                        : "340px"
                    }
                  >
                    <PlaygroundTemplate
                      playgroundInstanceId={instanceId}
                      appendedMessagesPath={appendedMessagesPath}
                      availablePaths={availablePaths}
                    />
                  </View>
                ))}
              </Flex>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Inputs & Output Card ──────────────── */}
          <div css={rightColumnCSS}>
            <div css={rightColumnHeaderCSS}>Inputs &amp; Output</div>
            <div css={rightColumnScrollCSS}>
              {isDatasetMode ? (
                <Suspense fallback={<Loading />}>
                  <PlaygroundDatasetSection
                    key={datasetId}
                    datasetId={datasetId}
                    splitIds={splitIds}
                  />
                </Suspense>
              ) : (
                <View padding="size-200">
                  <Flex direction="column" gap="size-300">
                    {/* Input Variables Section */}
                    {templateFormat !== TemplateFormats.NONE && (
                      <Flex direction="column" gap="size-100">
                        <div css={sectionLabelCSS}>Input Variables</div>
                        <PlaygroundInput />
                      </Flex>
                    )}

                    {/* Output Section */}
                    <Flex direction="column" gap="size-100">
                      <div css={sectionLabelCSS}>Output</div>
                      <div css={outputsRowScrollCSS}>
                        {instanceIds.map((instanceId) => (
                          <div
                            key={`${instanceId}-output`}
                            css={css`
                              flex: 1 1 0px;
                              min-width: ${numInstances === 1
                                ? "100%"
                                : numInstances === 2
                                ? "calc(50% - 6px)"
                                : "220px"};
                              box-sizing: border-box;
                            `}
                          >
                            <PlaygroundOutput
                              playgroundInstanceId={instanceId}
                            />
                          </div>
                        ))}
                      </div>
                    </Flex>

                    {/* Comparison Summary (Temporarily commented out as requested)
                    {numInstances > 1 && (
                      <Flex direction="column" gap="size-100">
                        <div css={sectionLabelCSS}>Comparison Summary</div>
                        <PlaygroundComparisonSummary />
                      </Flex>
                    )}
                    */}
                  </Flex>
                </View>
              )}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════
            BOTTOM CONTROLS BAR
          ══════════════════════════════════════════════════════════ */}
        <div css={bottomToolbarCSS}>
          {/* Left side controls: Format pills + API Keys + Run button */}
          <Flex direction="row" gap="size-150" alignItems="center">
            <TemplateFormatRadioGroup size="S" />
            <div css={toolbarDividerCSS} aria-hidden="true" />
            <PlaygroundCredentialsDropdown />
            <div css={toolbarDividerCSS} aria-hidden="true" />
            <PlaygroundRunButton />
          </Flex>

          {/* Right side controls: Ready status indicator */}
          <Flex direction="row" gap="size-150" alignItems="center">
            {!isRunning && (
              <Flex direction="row" gap="size-50" alignItems="center">
                <span css={readyDotCSS} />
                <Text size="XS" color="text-700">
                  Ready
                </Text>
              </Flex>
            )}
          </Flex>
        </div>
      </div>

      {runningExperiment ? (
        <ConfirmExperimentNavigationDialog
          blocker={blocker}
          experimentId={runningExperiment.id}
          isEphemeral={runningExperiment.isEphemeral}
        />
      ) : (
        <ConfirmNavigationDialog
          blocker={blocker}
          message="You have unsaved changes. Are you sure you want to leave?"
        />
      )}
    </Fragment>
  );
}
